import React, { useEffect, useState } from "react";
import { COMPANY } from "@/data/site";
import { Zap } from "lucide-react";

const KEY = "vps_gates_seen_v2";

export default function VoltechGatesIntro({ onOpen }) {
  const [phase, setPhase] = useState("hidden");
  // phases: hidden -> visible -> opening -> done

  useEffect(() => {
    const seen = sessionStorage.getItem(KEY);
    if (seen) {
      onOpen && onOpen();
      return;
    }
    setPhase("visible");
    const t1 = setTimeout(() => setPhase("opening"), 1400);
    const t2 = setTimeout(() => {
      sessionStorage.setItem(KEY, "1");
      setPhase("done");
      onOpen && onOpen();
    }, 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onOpen]);

  const skip = () => {
    sessionStorage.setItem(KEY, "1");
    setPhase("done");
    onOpen && onOpen();
  };

  if (phase === "hidden" || phase === "done") return null;

  return (
    <div
      className="fixed inset-0 z-[100] pointer-events-auto"
      data-testid="voltech-gates-intro"
      style={{ background: "#0a0a0c" }}
    >
      {/* Center logo pulse */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-500 ${
          phase === "opening" ? "opacity-0" : "opacity-100"
        }`}
        data-testid="gates-brand"
      >
        <img
          src={COMPANY.logo}
          alt="VPS"
          className="w-24 h-24 md:w-32 md:h-32 object-contain bg-white p-2 vps-pulse"
        />
        <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.4em] text-[#e63946]">
          Voltech Power Solutions
        </div>
        <div className="mt-2 font-display text-2xl md:text-3xl uppercase text-white tracking-tight">
          Opening the gates…
        </div>
      </div>

      {/* Left gate */}
      <div
        className="absolute inset-y-0 left-0 w-1/2 border-r-2 border-[#e63946] flex items-center justify-end pr-6 md:pr-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(90deg, #0a0a0c 0%, #121215 40%, #1a1a1f 80%, #2a2a30 100%)",
          transform: phase === "opening" ? "translateX(-100%)" : "translateX(0)",
          transition: "transform 1.6s cubic-bezier(0.7, 0, 0.3, 1)",
          boxShadow: "inset -6px 0 20px rgba(230,57,70,0.15)",
        }}
        data-testid="gate-left"
      >
        <div className="font-display text-6xl md:text-9xl uppercase text-white/10 tracking-tighter select-none leading-none">
          VOL
        </div>
      </div>

      {/* Right gate */}
      <div
        className="absolute inset-y-0 right-0 w-1/2 border-l-2 border-[#e63946] flex items-center justify-start pl-6 md:pl-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(-90deg, #0a0a0c 0%, #121215 40%, #1a1a1f 80%, #2a2a30 100%)",
          transform: phase === "opening" ? "translateX(100%)" : "translateX(0)",
          transition: "transform 1.6s cubic-bezier(0.7, 0, 0.3, 1)",
          boxShadow: "inset 6px 0 20px rgba(230,57,70,0.15)",
        }}
        data-testid="gate-right"
      >
        <div className="font-display text-6xl md:text-9xl uppercase text-white/10 tracking-tighter select-none leading-none">
          TECH
        </div>
      </div>

      {/* Center seam glow at first */}
      <div
        className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, transparent, #e63946 50%, transparent)",
          filter: "blur(2px)",
          opacity: phase === "opening" ? 0 : 1,
          transition: "opacity 500ms ease",
        }}
      />

      {/* Skip */}
      <button
        onClick={skip}
        className="absolute top-5 right-5 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white border border-white/20 hover:border-white px-3 py-2 bg-black/40"
        data-testid="gates-skip-btn"
      >
        Skip intro →
      </button>

      {/* Bolt streak */}
      <Zap
        size={38}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[#e63946] vps-pulse pointer-events-none"
      />
    </div>
  );
}
